import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { isEditableTarget, isHelpShortcut } from './supportWidgetUtils'

const SupportContext = createContext(null)

export function SupportProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)

  const open = useCallback(() => {
    setHasOpened(true)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggle = useCallback(() => {
    setIsOpen((current) => {
      if (!current) setHasOpened(true)
      return !current
    })
  }, [])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault()
        close()
        return
      }
      if (event.defaultPrevented || isEditableTarget(event.target)) return
      if (!isHelpShortcut(event)) return
      event.preventDefault()
      toggle()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, close, toggle])

  const value = useMemo(
    () => ({ isOpen, hasOpened, open, close, toggle }),
    [isOpen, hasOpened, open, close, toggle]
  )

  return (
    <SupportContext.Provider value={value}>{children}</SupportContext.Provider>
  )
}

export const useSupport = () => {
  const context = useContext(SupportContext)
  if (!context) {
    throw new Error('useSupport must be used within a SupportProvider')
  }
  return context
}
